import { z } from 'zod';
import { cuid, isoDate, paginacao } from './primitives.js';
import type { CustoAquisicaoResponse } from './custos-aquisicao.js';
import type { FichaResponse } from './fichas-tecnicas.js';
import type { ItemTributoResponse } from './itens-tributos.js';

/**
 * Tipos de item (Sessao 6). Imutavel depois de salvo (API e banco).
 *  - PRODUTO e SERVICO: tem ficha tecnica (custo = ficha escolhida na data);
 *  - MERCADORIA (revenda) e os quatro insumos (MATERIA_PRIMA, MATERIAL_SECUNDARIO, EMBALAGEM, MATERIAL_AUXILIAR): COMPRADOS, tem
 *    custo de aquisicao com vigencia (custo = aquisicao vigente na data) e NUNCA ficha.
 * Valores dos insumos = os do XARP (rawMaterial, secondaryMaterial, package, auxiliaryMaterial).
 *  - SERVICO_TERCEIRO (Sessao 16): servico comprado de terceiro; comprado, compoe ficha, nao comerciavel por padrao; aparece como
 *    "Servicos de terceiros" no custo da ficha.
 */

/** Classificacao do PRODUTO (Sessao 16): intermediario | conjunto | acabado. Nula nos demais tipos. */
export const classificacaoProduto = z.enum(['INTERMEDIARIO', 'CONJUNTO', 'ACABADO']);
export type ClassificacaoProduto = z.infer<typeof classificacaoProduto>;

export const itemTipo = z.enum(['PRODUTO', 'MERCADORIA', 'SERVICO', 'MATERIA_PRIMA', 'MATERIAL_SECUNDARIO', 'EMBALAGEM', 'MATERIAL_AUXILIAR', 'SERVICO_TERCEIRO']);
export type ItemTipo = z.infer<typeof itemTipo>;

export const TIPOS_COM_FICHA: readonly ItemTipo[] = ['PRODUTO', 'SERVICO'];
export const TIPOS_COMPRADOS: readonly ItemTipo[] = ['MERCADORIA', 'MATERIA_PRIMA', 'MATERIAL_SECUNDARIO', 'EMBALAGEM', 'MATERIAL_AUXILIAR', 'SERVICO_TERCEIRO'];
/** Compram-se, compoem ficha e nao sao comerciaveis por padrao. SERVICO_TERCEIRO (Sessao 16) e o servico comprado de terceiro. */
export const TIPOS_INSUMO: readonly ItemTipo[] = ['MATERIA_PRIMA', 'MATERIAL_SECUNDARIO', 'EMBALAGEM', 'MATERIAL_AUXILIAR', 'SERVICO_TERCEIRO'];
export const temFicha = (t: ItemTipo): boolean => TIPOS_COM_FICHA.includes(t);
export const ehComprado = (t: ItemTipo): boolean => TIPOS_COMPRADOS.includes(t);

/**
 * Padrao das duas flags por tipo (os do XARP ao escolher o tipo, stockable-form.component.ts:276-300): produto e mercadoria nao
 * compoem ficha e sao comerciaveis; os insumos compoem ficha e nao sao comerciaveis. SERVICO como PRODUTO. Editaveis depois.
 */
export function padroesDoTipo(t: ItemTipo): { compoeFichaTecnica: boolean; comerciavel: boolean } {
  return TIPOS_INSUMO.includes(t) ? { compoeFichaTecnica: true, comerciavel: false } : { compoeFichaTecnica: false, comerciavel: true };
}

/** Fator da unidade de custo para a de venda: decimal maior que zero (ate 6 casas). */
export const fatorUnidade = z.string().regex(/^\d{1,9}(\.\d{1,6})?$/, 'Fator inválido (decimal até 6 casas)').refine((v) => Number(v) > 0, 'O fator deve ser maior que zero');

const queryBool = z
  .enum(['true', 'false'])
  .transform((v) => v === 'true');

export const criarItemSchema = z.object({
  tipo: itemTipo,
  codigo: z.string().trim().min(1, 'Informe o código').max(40, 'O código tem no máximo 40 caracteres'),
  descricao: z.string().trim().min(1, 'Informe o nome do item').max(200, 'O nome tem no máximo 200 caracteres'),
  unidade: z.string().trim().min(1, 'Escolha a unidade').max(10, 'A unidade tem no máximo 10 caracteres'),
  zfm: z.boolean().default(false),
  /** Omitido = padrao do tipo (`padroesDoTipo`). */
  compoeFichaTecnica: z.boolean().optional(),
  comerciavel: z.boolean().optional(),
  /** So PRODUTO (400 nos demais tipos). */
  classificacao: classificacaoProduto.optional(),
  /** Unidade de custo (t) e o fator para a unidade de venda (t -> kg = 1000). Padrao: sem unidade de custo, fator 1. */
  unidadeCusto: z.string().trim().min(1).max(10).optional(),
  fatorCustoVenda: fatorUnidade.optional(),
  /** Categoria do cadastro de categorias (Sessao 28); omitido = sem categoria. */
  categoriaId: cuid.nullish(),
});
export type CriarItemDto = z.infer<typeof criarItemSchema>;

export const atualizarItemSchema = z
  .object({
    descricao: z.string().trim().min(1, 'Informe o nome do item').max(200, 'O nome tem no máximo 200 caracteres'),
    unidade: z.string().trim().min(1, 'Escolha a unidade').max(10, 'A unidade tem no máximo 10 caracteres'),
    zfm: z.boolean(),
    ativo: z.boolean(),
    compoeFichaTecnica: z.boolean(),
    comerciavel: z.boolean(),
    classificacao: classificacaoProduto.nullable(),
    unidadeCusto: z.string().trim().min(1).max(10).nullable(),
    fatorCustoVenda: fatorUnidade,
    categoriaId: cuid.nullable(),
  })
  .partial()
  .refine((v) => Object.keys(v).length > 0, 'Informe ao menos um campo');
export type AtualizarItemDto = z.infer<typeof atualizarItemSchema>;

export const listarItensSchema = paginacao.extend({
  tipo: itemTipo.optional(),
  ativo: queryBool.optional(),
  q: z.string().trim().min(1).max(100).optional(),
  /** true = so itens sem custo hoje (PRODUTO/SERVICO sem ficha escolhida; comprado sem aquisicao vigente hoje); false = so com custo. */
  semCusto: queryBool.optional(),
  comerciavel: queryBool.optional(),
  compoeFichaTecnica: queryBool.optional(),
  classificacao: classificacaoProduto.optional(),
  /** Categoria (com as subcategorias dela) ou `SEM` = itens sem categoria. */
  categoriaId: z.union([cuid, z.literal('SEM')]).optional(),
});
export type ListarItensQuery = z.infer<typeof listarItensSchema>;

export interface ItemResponse {
  /** Vem do Protheus (rotina de produtos em IMPORTAR): codigo, descricao, unidade e situacao so se editam la. */
  doProtheus?: boolean;
  id: string;
  tipo: z.infer<typeof itemTipo>;
  codigo: string;
  descricao: string;
  unidade: string;
  zfm: boolean;
  ativo: boolean;
  arquivadoEm: string | null;
  createdAt: string;
  updatedAt: string;
  /** O item pode ser componente de ficha. Sempre presente na resposta da API. */
  compoeFichaTecnica?: boolean;
  /** Aparece na lista de precos, na formacao e no orcamento. Sempre presente na resposta da API. */
  comerciavel?: boolean;
  /** So PRODUTO. */
  classificacao?: ClassificacaoProduto | null;
  /** Unidade de custo (a da ficha) e o fator para a unidade de venda; carteira e compras convertem com ele. */
  unidadeCusto?: string | null;
  fatorCustoVenda?: string;
  /** Categoria do item (null = sem categoria) e o caminho legivel dela. */
  categoriaId?: string | null;
  categoriaCaminho?: string | null;
  /**
   * "Sem custo" HOJE: PRODUTO/SERVICO sem ficha escolhida; comprado sem custo de aquisicao vigente hoje. Nao e valor em R$ (vale com
   * `item.ver`). Erro de resolucao da ficha (ciclo, CPM ausente...) NAO entra aqui. Sempre presente na resposta da API.
   */
  semCusto?: boolean;
}

/**
 * Formulario do cadastro de item (tela /cadastros/itens/$id e /novo). Fonte unica das regras do formulario:
 * item novo valida com `criarItemSchema`; item existente (codigo e tipo sao imutaveis) com `atualizarItemSchema`.
 * Este schema junta os dois conjuntos de campos so para o react-hook-form; o corpo enviado a API e sempre
 * `criarItemSchema` (POST) ou `atualizarItemSchema` (PATCH).
 */
export const itemFormSchema = criarItemSchema.extend({ zfm: z.boolean(), ativo: z.boolean() });
export type ItemFormValores = z.infer<typeof itemFormSchema>;

/** Situacao do cadastro: ativo, inativo (ativo=false sem arquivar) ou arquivado (soft delete). */
export const situacaoItem = z.enum(['ATIVO', 'INATIVO', 'ARQUIVADO']);
export type SituacaoItem = z.infer<typeof situacaoItem>;

/** GET /itens/pagina: lista paginada com total real do filtro, ordenada por codigo. */
export const listarItensPaginaSchema = paginacao.extend({
  tipo: itemTipo.optional(),
  situacao: situacaoItem.optional(),
  q: z.string().trim().min(1).max(100).optional(),
  /** true = so itens sem custo hoje; false = so com custo (ver `ItemResponse.semCusto`). */
  semCusto: queryBool.optional(),
  comerciavel: queryBool.optional(),
  compoeFichaTecnica: queryBool.optional(),
  classificacao: classificacaoProduto.optional(),
  categoriaId: z.union([cuid, z.literal('SEM')]).optional(),
});
export type ListarItensPaginaQuery = z.infer<typeof listarItensPaginaSchema>;

export interface ItensPaginaResponse {
  itens: ItemResponse[];
  /** Registros do filtro (independe da pagina). */
  total: number;
  limit: number;
  offset: number;
}

/** GET /itens/vizinhos: posicao "n de total" e vizinhos de um item na ordem da lista filtrada. */
export const vizinhosItemSchema = listarItensPaginaSchema.omit({ limit: true, offset: true }).extend({ id: cuid });
export type VizinhosItemQuery = z.infer<typeof vizinhosItemSchema>;

export interface VizinhosItemResponse {
  /** Posicao 1-based do item na lista filtrada (ou onde ele entraria, se `naLista` for false). */
  posicao: number;
  total: number;
  /** false quando o item nao atende ao filtro (ex.: recem arquivado com filtro "Ativos"). */
  naLista: boolean;
  anteriorId: string | null;
  proximoId: string | null;
}

/** GET /itens/:id/resumo: o que vigora numa data e o que foi praticado, tudo ja resolvido pela API. */
export const resumoItemSchema = z.object({ data: isoDate.optional() });
export type ResumoItemQuery = z.infer<typeof resumoItemSchema>;

export interface PrecoPraticadoItemResponse {
  id: string;
  tabelaId: string;
  tabelaNome: string;
  condicaoId: string;
  condicaoNome: string;
  price: string;
  recordedAt: string;
}

export interface ResumoItemResponse {
  data: string;
  tributoVigente: ItemTributoResponse | null;
  /** Os tipos COMPRADOS (MERCADORIA e insumos) tem custo de aquisicao; PRODUTO/SERVICO e sempre null. Omitido sem custo.ver. */
  custoAquisicaoVigente?: CustoAquisicaoResponse | null;
  /** Omitida sem ficha.ver; sem custo.ver (PCP) vem a estrutura sem R$. */
  fichaEscolhida?: FichaResponse | null;
  /** Mais recentes primeiro (ate 10). Omitidos sem preco.ver (PCP). */
  precosPraticados?: PrecoPraticadoItemResponse[];
}
